import React, { ReactNode } from "react";
import { ChartEvent, ChartOptions } from "chart.js";
import { get, map, reduce } from "lodash";
import { shorten } from "@backpacker/number";
import Tooltip from "@charty/tooltip/chartjs-simple-serie";
import { Color } from "@glossy";
import { content } from "@glossy/theme";
import { AbstractDate, Interval } from "@linkfluence/models/date";
import { getOnChangeCanvasCursorEvent } from "@linkfluence/models/events/onChangeCanvasCursor";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { isFloat } from "@tootools/number";
import { Serie } from "../models";
import {
  closeCustomTooltip,
  openCustomTooltip,
} from "../tooltip/chartjs-tooltip-root";
import {
  formatDateTitle,
  formatXAxisBottomLabel,
  formatXAxisTopLabel,
} from "../utils";

function getTotalPublications(series: Serie[]): number[] {
  return reduce(
    series,
    (totalByDate, { data }) => {
      return reduce(
        data,
        (total, val, i) => {
          total[i] = (total[i] || 0) + get(val, "count");
          return total;
        },
        totalByDate
      );
    },
    []
  );
}

type ChartTooltip = {
  count: number;
  label: string;
  color: string;
  date: AbstractDate;
};

export function getOptions(
  allDates: AbstractDate[],
  timeUnit: Interval,
  theme,
  locale: Locale,
  timezone: Timezone,
  onClick: (datasetIndex: number, index: number) => void,
  series: Serie[],
  chartTooltip?: (props: ChartTooltip[]) => ReactNode,
  isNormalized?: boolean,
  scale?: "logarithmic" | "linear",
  disableAnimation?: boolean,
  isFullscreen: boolean = false,
  metricLabel?: string
): ChartOptions<"bar"> {
  return {
    animation: {
      duration: disableAnimation ? 0 : 1000,
    },
    onClick: function (evt) {
      if (onClick) {
        if (!isFullscreen) {
          closeCustomTooltip();
        }
        const elements = this.getElementsAtEventForMode(
          evt,
          "nearest",
          { intersect: true },
          false
        );
        if (elements && elements[0]) {
          onClick(elements[0].datasetIndex, elements[0].index);
        }
      }
    },
    scales: {
      y: {
        type: scale,
        stacked: true,
        beginAtZero: true,
        max: isNormalized ? 100 : undefined,
        ticks: {
          color: content({ theme }, "secondary"),
          callback: (value: any) => {
            const val = shorten(locale, value);
            return isFloat(value) ? undefined : isNormalized ? val + "%" : val;
          },
        },
        grid: {
          display: true,
          color: content({ theme }, "tertiary"),
        },
      },
      x: {
        offset: true,
        stacked: true,
        ticks: {
          color: content({ theme }, "secondary"),
          callback: formatXAxisBottomLabel(timeUnit, locale, timezone, series),
        },
        grid: {
          color: (context) =>
            context.index === 0
              ? content({ theme }, "tertiary")
              : "transparent",
          offset: true,
          z: 1,
        },
      },
      x2: {
        offset: true,
        position: "top",
        ticks: {
          autoSkip: false,
          maxRotation: 0,
          color: content({ theme }, "secondary"),
          callback: formatXAxisTopLabel(timeUnit, locale, timezone, series),
        },
        border: {
          display: false,
        },
        grid: {
          display: true,
          color: content({ theme }, "tertiary"),
          lineWidth: 0.5,
          offset: false,
        },
      },
    },

    onHover: function (e: ChartEvent) {
      if (onClick && e.native.target instanceof HTMLElement) {
        const point = this.getElementsAtEventForMode(
          e,
          "nearest",
          { intersect: true },
          false
        );
        const cursor = point.length ? "var(--cursor-magic-wand)" : "default";
        if (e.native.target.style.cursor !== cursor) {
          e.native.target.style.cursor = cursor;
          e.native.target.dispatchEvent(getOnChangeCanvasCursorEvent());
        }
      }
    },
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        caretPadding: 24,
        enabled: isFullscreen,
        padding: 24,
        backgroundColor: Color.Black,
        titleColor: Color.White,
        titleFont: { size: 14 },
        bodyColor: Color.White,
        bodyFont: { size: 14 },
        bodySpacing: 12,
        callbacks: {
          title: (tooltipModel) => {
            return formatDateTitle(
              timeUnit,
              tooltipModel[0].label,
              locale,
              timezone
            );
          },
          beforeBody: (tooltipModel) => {
            const data = tooltipModel[0].chart.data;

            const total = data.datasets.reduce(
              (acc, cur) =>
                (acc += cur.data[tooltipModel[0].dataIndex] as number),
              0
            );
            return `${shorten(locale, total)} ${metricLabel}`;
          },
          label: (tooltipModel) => {
            const dataIdx = tooltipModel["dataIndex"];
            const data = tooltipModel.chart.data;
            const idx = tooltipModel["datasetIndex"];
            return ` ${data["datasets"][idx].label as string}: ${shorten(
              locale,
              data["datasets"][idx].data[dataIdx] as number
            )}`;
          },
        },
        external: (context) => {
          if (isFullscreen) {
            return null;
          }

          const { chart, tooltip } = context;

          if (!tooltip.opacity) {
            closeCustomTooltip();
            return;
          }

          const dataPoints = tooltip.dataPoints;

          const totalPublications = getTotalPublications(series);
          const dataSeries = map(dataPoints, (dataPoint) => {
            const dataSetIndex = dataPoint.datasetIndex;
            const index = dataPoint.dataIndex;
            return {
              color: series[dataSetIndex].color,
              label: series[dataSetIndex].label,
              date: allDates[index],
              count: series[dataSetIndex].data[index].count,
              total: totalPublications[index],
            };
          });

          openCustomTooltip(
            tooltip,
            chart,
            chartTooltip ? (
              chartTooltip(dataSeries)
            ) : (
              <Tooltip series={dataSeries} locale={locale} />
            )
          );
        },
      },
      //@ts-ignore: chartjs-plugin-stacked100 is properly imported and registered on the chart
      stacked100: { enable: isNormalized },
      //@ts-ignore: datalabels is properly imported and registered on the chart
      datalabels: {
        display: false,
      },
    },
  };
}
